import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Spinner } from "react-bootstrap";
import api from "@/api/api.js";
import i18n from "@/i18n.js";
import { TAX_REGIMES } from "@/config/satCatalogs.js";
import SignatureStatus from "@/pages/shared/sections/SignatureStatus.jsx";
import CertificateTaxStatus from "@/pages/shared/sections/CertificateTaxStatus.jsx";

const regimeLabel = (code) =>
  TAX_REGIMES.find((r) => r.code === code)?.label ?? (code || "—");

const Field = ({ label, value }) => (
  <div className="col-md-6 mb-3">
    <small className="text-muted d-block">{label}</small>
    <span>{value || "—"}</span>
  </div>
);

const CompanyShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/companies/${id}`)
      .then(({ data }) => setCompany(data))
      .catch(() => setError(i18n.t("ui.accountLoadError")))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="col">
        <div className="card">
          <div className="card-body text-center py-5">
            <Spinner animation="border" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="col">
        <Alert variant="danger">{error || i18n.t("ui.accountNotFound")}</Alert>
      </div>
    );
  }

  const address = [
    company.street,
    company.exteriorNumber && `#${company.exteriorNumber}`,
    company.interiorNumber && `Int. ${company.interiorNumber}`,
    company.suburb,
    company.municipality,
    company.state,
    company.postalCode && `C.P. ${company.postalCode}`,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <div className="col">
        <div className="card">
          <div className="card-body">
            <div className="border-bottom pb-2 mb-3 d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">{company.name}</h5>
                <small className="text-muted">{company.taxIdentification}</small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/companies/${id}/edit`)}
                >
                  <i className="bi bi-pencil me-1" />
                  Editar
                </Button>
              </div>
            </div>

            <h6 className="text-primary fw-bold mb-2">Datos fiscales</h6>
            <div className="row">
              <Field label="RFC" value={company.taxIdentification} />
              <Field
                label="Tipo de persona"
                value={company.type ? `Persona ${company.type}` : "—"}
              />
              <Field
                label={company.type === "moral" ? "Razón social" : "Nombre"}
                value={company.name}
              />
              {company.type === "moral" && (
                <Field label="Nombre comercial" value={company.businessName} />
              )}
              <Field
                label="Régimen fiscal"
                value={regimeLabel(company.taxRegime)}
              />
            </div>

            <h6 className="text-primary fw-bold mb-2 mt-2">Domicilio fiscal</h6>
            <div className="row">
              <Field label="Dirección" value={address} />
            </div>

            <h6 className="text-primary fw-bold mb-2 mt-2">Teléfono y correo</h6>
            <div className="row">
              <Field label="Teléfono" value={company.phone} />
              <Field label="Correo" value={company.email} />
            </div>
          </div>
        </div>
      </div>

      <div className="col">
        <div className="card">
          <div className="card-body">
            <div className="border-bottom pb-2 mb-3">
              <h5 className="mb-0">Firma electrónica (FIEL)</h5>
              <small className="text-muted">
                Estado y vigencia de tu certificado.
              </small>
            </div>
            <SignatureStatus companyId={id} />
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="border-bottom pb-2 mb-3">
              <h5 className="mb-0">Constancia de situación fiscal</h5>
              <small className="text-muted">
                Documento cargado y descarga.
              </small>
            </div>
            <CertificateTaxStatus companyId={id} />
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyShow;
